import { cn, IconButton, iconButtonVariants } from '@risksmart-app/atomic-ui';
import type { MouseEvent, ReactNode } from 'react';

import { ICON_BUTTON_SIZE } from '../types';

/**
 * How the button reveals its title:
 * - `'tooltip'` — a tooltip below the icon on hover (the icon never moves).
 * - `'slideout'` — the title slides out to the left of the icon on hover,
 *   growing the button leftward (icon stays put).
 * - `'none'` — icon only, no visible title (the accessible name still lives on
 *   the button's `aria-label`). Used when the user turns action labels off.
 */
export type RevealMode = 'tooltip' | 'slideout' | 'none';

interface Props {
  /** Accessible name (icon button `aria-label`) and the visible revealed text. */
  label: string;
  /** How the title is revealed. See {@link RevealMode}. */
  reveal: RevealMode;
  /**
   * Takes the event so an anchor-rendered action can intercept a plain left
   * click for client-side routing while leaving modifier-clicks to the browser.
   * Command actions can keep ignoring it.
   */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /**
   * Renders the button as an anchor to this href instead of a `<button>`. For
   * actions that are navigations rather than commands — a create affordance that
   * routes to a form, say — so the control keeps link semantics (middle-click,
   * open-in-new-tab, `role="link"`). Pair with `onClick` for a router's
   * `handleFollow` if client-side navigation is wanted.
   */
  href?: string;
  disabled?: boolean;
  /** Icon content (kept fixed at the button's right edge in slideout mode). */
  children: ReactNode;
}

/**
 * The tray control: an `IconButton` for a command, a plain anchor wearing the same
 * skin for a navigation.
 *
 * A navigation is not an `IconButton` with an `href`. Base UI's button keeps
 * `role="button"` even when its `render` prop supplies an anchor, so the control
 * would announce as a button, take Space to activate, and — the reason this matters
 * here — stop matching a `role="link"` query. An anchor with `iconButtonVariants`
 * is visually identical and semantically right.
 */
const TrayControl = ({
  label,
  href,
  onClick,
  disabled,
  className,
  children,
}: Pick<Props, 'label' | 'href' | 'onClick' | 'disabled' | 'children'> & {
  className?: string;
}) =>
  href === undefined ? (
    <IconButton
      className={className}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </IconButton>
  ) : (
    <a
      className={cn(iconButtonVariants(), className)}
      data-slot={'icon-button'}
      aria-label={label}
      // A native anchor has no `disabled`: it would stay focusable and still follow
      // its href. Carry the state in `aria-disabled`, take it out of the tab order,
      // and swallow the click — the styling already comes from the shared skin.
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      href={href}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();

          return;
        }
        onClick?.(event);
      }}
    >
      {children}
    </a>
  );

/**
 * A tray icon button whose title is revealed on hover — the shared reveal
 * behaviour behind every primary object-toolbar action (primary actions plus
 * Save / Approve / Cancel).
 *
 * Three modes (see {@link RevealMode}):
 * - `'tooltip'` (default) — icon only; the title appears as a tooltip below the
 *   icon (white surface, `aria-hidden`). The icon never moves.
 * - `'slideout'` — the title is literal button content sitting to the icon's
 *   left, collapsed to zero width; on hover it expands rightward-anchored so the
 *   button grows to the **left** while the icon stays put.
 * - `'none'` — icon only, no reveal element at all.
 *
 * The accessible name is always the icon button's `aria-label`; the visible
 * title is decorative (`aria-hidden`).
 */
export const RevealButton = ({
  label,
  reveal,
  onClick,
  href,
  disabled,
  children,
}: Props) => {
  if (reveal === 'none') {
    // Labels off: a plain icon button. The accessible name is still carried by
    // `aria-label`, so screen-reader users keep the title even with no visible
    // tooltip/slideout.
    return (
      <TrayControl
        className={ICON_BUTTON_SIZE}
        label={label}
        href={href}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </TrayControl>
    );
  }

  if (reveal === 'slideout') {
    // The label is real button content to the icon's left. The button is a
    // right-justified flex row that starts icon-sized (`min-w-8`) and grows
    // leftward as the label expands from zero width on hover/focus. Because the
    // tray is right-aligned, the growth visually pushes leftward.
    //
    // The reveal animates a CSS grid track from `0fr` → `1fr` rather than a
    // `max-width` cap. A fixed cap (e.g. `max-w-[200px]`) overshoots the label's
    // real width, so the visible reveal finishes partway through the timeline
    // and the collapse sits idle before snapping — asymmetric, and with two
    // labels animating at once the tray width overshoots and settles (the
    // "bump"). `0fr → 1fr` resolves to the exact content width, so in and out
    // are symmetric and the same real duration for any label length. The inner
    // span's `overflow-hidden` gives the grid item a zero automatic minimum size
    // so the `0fr` track can fully collapse.
    return (
      <TrayControl
        label={label}
        href={href}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          ICON_BUTTON_SIZE,
          // px-1 (not the inherited p-1.5) so the collapsed width is exactly the
          // icon + 8px = 32px — identical to the fixed size-8 tooltip/none
          // buttons (whose 24px icon sits in a 32px box, i.e. 4px each side). A
          // w-auto button with p-1.5 would render 36px and jump 4px when the user
          // toggles labels on/off. The label's own breathing room lives on the
          // label (px-2), inside the collapsing clipper, so it adds nothing here.
          'group/tbtn w-auto min-w-8 justify-end gap-0 overflow-hidden px-1'
        )}
      >
        <span
          aria-hidden={true}
          className={cn(
            // Reveal animates a grid track 0fr → content width (1fr). The tray
            // renders its buttons edge-to-edge (gap-0), so :hover hands over
            // between adjacent buttons on a single frame — the leaving label's
            // collapse and the entering label's expand start together and, sharing
            // this curve/duration, sum to a monotonic tray width (no dip, no
            // overshoot). No exit delay needed.
            'grid grid-cols-[0fr] transition-[grid-template-columns] duration-150 ease-in-out',
            'group-hover/tbtn:grid-cols-[1fr] group-focus-visible/tbtn:grid-cols-[1fr]'
          )}
        >
          {/* Unpadded clipper — the grid item. `overflow-hidden` gives it a zero
              automatic minimum size so the 0fr track fully collapses, AND it
              clips the padded label inside it, so the label's px-2 contributes
              nothing while collapsed (no toggling → the reveal stays smooth). */}
          <span className={cn('overflow-hidden')}>
            <span
              className={cn(
                // px-2 is always on, but it lives inside the clipper above, so it
                // only takes space as the track opens — it grows in lockstep with
                // the width reveal rather than popping in.
                'block select-none whitespace-nowrap px-2 text-md',
                // Fade in lockstep with the width reveal — same 150ms/easing, and
                // opacity doesn't affect layout so it can't reintroduce the bump.
                'opacity-0 transition-opacity duration-150 ease-in-out',
                'group-hover/tbtn:opacity-100 group-focus-visible/tbtn:opacity-100'
              )}
            >
              {label}
            </span>
          </span>
        </span>
        {children}
      </TrayControl>
    );
  }

  return (
    <div className={cn('group/tbtn relative flex items-center')}>
      <TrayControl
        className={ICON_BUTTON_SIZE}
        label={label}
        href={href}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </TrayControl>
      {/* Tooltip below the icon, centred, revealed on hover/focus. White
          surface via the `neutral` semantic token. `select-none` keeps the
          label out of the selection when the trigger button is clicked. */}
      <span
        aria-hidden={true}
        className={cn(
          'pointer-events-none select-none absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 whitespace-nowrap',
          'rounded-md border border-neutral-border-minimal bg-neutral px-2 py-1 text-base text-neutral-foreground shadow-sm',
          'opacity-0 transition-opacity duration-150',
          'group-hover/tbtn:opacity-100 group-focus-within/tbtn:opacity-100'
        )}
      >
        {label}
      </span>
    </div>
  );
};
